import { isTouchDevice, prefersReducedMotion } from '@theme/utilities';

const FILL_SELECTOR = '[data-fill]';
const BUTTON_SELECTOR = `.button:has(> ${FILL_SELECTOR}), .button-secondary:has(> ${FILL_SELECTOR}), .button-custom:has(> ${FILL_SELECTOR})`;
const DURATION = 600;
const EASING = 'cubic-bezier(0.25, 1, 0.5, 1)';
const RESTING_TRANSFORM = 'translate3d(0, -76%, 0)';

/** @type {WeakSet<HTMLElement>} */
const hoveredButtons = new WeakSet();

/**
 * Whether the fill hover should run.
 * @returns {boolean}
 */
function isHoverEnabled() {
  return (
    document.body?.getAttribute('data-button-hover') === 'standard' &&
    !isTouchDevice() &&
    !prefersReducedMotion() &&
    window.matchMedia('(pointer: fine)').matches
  );
}

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function getHoverButton(target) {
  if (!(target instanceof Element)) return null;

  const button = target.closest(BUTTON_SELECTOR);
  if (!(button instanceof HTMLElement)) return null;
  if (button.matches(':disabled, [disabled], [aria-disabled="true"]')) return null;

  return button;
}

/**
 * @param {HTMLElement} button
 * @returns {HTMLElement | null}
 */
function getFill(button) {
  const fill = button.querySelector(`:scope > ${FILL_SELECTOR}`);
  return fill instanceof HTMLElement ? fill : null;
}

/**
 * @param {HTMLElement} fill
 */
function cancelFillAnimations(fill) {
  fill.getAnimations().forEach((animation) => {
    if (typeof animation.commitStyles === 'function') {
      animation.commitStyles();
    }
    animation.cancel();
  });
}

/**
 * @param {HTMLElement} fill
 * @returns {string}
 */
function getFillTransform(fill) {
  const transform = getComputedStyle(fill).transform;
  return transform === 'none' ? RESTING_TRANSFORM : transform;
}

/**
 * @param {HTMLElement} fill
 * @param {Keyframe[]} keyframes
 */
function animateFillFrom(fill, keyframes) {
  cancelFillAnimations(fill);

  fill.animate(keyframes, {
    duration: DURATION,
    easing: EASING,
    fill: 'forwards',
  });
}

/**
 * @param {HTMLElement} fill
 * @param {string} targetTransform
 */
function animateFillTo(fill, targetTransform) {
  cancelFillAnimations(fill);

  fill.animate([{ transform: getFillTransform(fill) }, { transform: targetTransform }], {
    duration: DURATION,
    easing: EASING,
    fill: 'forwards',
  });
}

/**
 * @param {HTMLElement} button
 */
function onEnter(button) {
  const fill = getFill(button);
  if (!fill) return;

  // Enter from below (76%) up into place (0%).
  animateFillFrom(fill, [
    { transform: 'translate3d(0, 76%, 0)' },
    { transform: 'translate3d(0, 0%, 0)' },
  ]);
}

/**
 * @param {HTMLElement} button
 */
function onLeave(button) {
  const fill = getFill(button);
  if (!fill) return;

  // Exit upward to resting position (-76%).
  animateFillTo(fill, RESTING_TRANSFORM);
}

/**
 * @param {MouseEvent} event
 */
function handleMouseOver(event) {
  if (!isHoverEnabled()) return;

  const button = getHoverButton(event.target);
  if (!button || hoveredButtons.has(button)) return;

  const related = event.relatedTarget;
  if (related instanceof Node && button.contains(related)) return;

  hoveredButtons.add(button);
  onEnter(button);
}

/**
 * @param {MouseEvent} event
 */
function handleMouseOut(event) {
  if (!isHoverEnabled()) return;

  const button = getHoverButton(event.target);
  if (!button || !hoveredButtons.has(button)) return;

  const related = event.relatedTarget;
  if (related instanceof Node && button.contains(related)) return;

  hoveredButtons.delete(button);
  onLeave(button);
}

document.addEventListener('mouseover', handleMouseOver);
document.addEventListener('mouseout', handleMouseOut);
